// First-time guided tour shown ONCE on a staff member's first visit to /today.
//
// Why this exists: when a housekeeper / nanny / driver accepts the WhatsApp
// invite link, sets a password, and lands on /today, they see four buckets
// (Right now / Up next / Waiting on me / Done today) with zero explanation.
// A Filipina housekeeper who has never used a task app opens it, doesn't
// know what to do, closes it, asks the owner. That kills the loop.
//
// Three slides. Plain language. No emojis (brand rule). On "Got it" or
// "Skip tour" we PATCH /v1/me { staff_tour_seen_at: "now" } so the marker
// is server-persisted (survives reinstall, new device, incognito).
//
// The parent (StaffToday) decides when to render — based on the server-side
// staff_tour_seen_at column being null. This component is dumb: it shows
// itself when mounted, persists the marker on close, then calls onClose.

import { useState } from 'react';
import { ApiError, api } from '../api';
import { useEscape } from '../components/ui/Sheet';
import { t } from '../i18n';

interface Props {
  fullName: string;
  avatarUrl?: string | null;
  // Parent gives us the close hook so it can also flip its local "tour open"
  // flag. We do the PATCH ourselves so a network failure here doesn't make
  // the parent think the tour was completed — we only call onClose on success
  // (or on user-initiated skip, which we treat the same as completion).
  onClose: () => void;
}

const TOTAL_SLIDES = 3;

export function StaffTour({ fullName, avatarUrl, onClose }: Props) {
  const [slide, setSlide] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function markSeenAndClose() {
    setBusy(true);
    setErr(null);
    try {
      await api('/v1/me', {
        method: 'PATCH',
        body: JSON.stringify({ staff_tour_seen_at: 'now' }),
      });
      onClose();
    } catch (e) {
      // Don't close on failure — the tour would silently come back on next
      // mount and the user would wonder if their action stuck. Show the
      // error and let them retry.
      setErr(e instanceof ApiError ? e.message : t('Failed'));
      setBusy(false);
    }
  }

  function next() {
    if (slide < TOTAL_SLIDES - 1) setSlide(slide + 1);
    else void markSeenAndClose();
  }

  const initials = makeInitials(fullName);

  // Escape closes like every other dialog in the app (Sheet's useEscape) —
  // same as tapping "Skip tour", but not while a PATCH is in flight.
  useEscape(() => {
    if (!busy) void markSeenAndClose();
  });

  return (
    <div
      className="modal-backdrop"
      // role+aria so screen readers announce it as a dialog instead of an
      // ambient overlay. dir="auto" lets Hebrew / Urdu content flip naturally
      // even inside an LTR page.
      // biome-ignore lint/a11y/useSemanticElements: a native <dialog> would have to be opened imperatively with showModal(); this tour is rendered conditionally by React and role="dialog" + aria-modal announces the same thing without changing the mount/unmount model.
      role="dialog"
      aria-modal="true"
      aria-label={t("Here's how famanage works for you.")}
    >
      <div className="modal-panel space-y-6 sm:max-w-lg" dir="auto">
        <div className="flex items-center justify-between">
          <Dots current={slide} total={TOTAL_SLIDES} />
          <button
            type="button"
            onClick={() => void markSeenAndClose()}
            disabled={busy}
            className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            {t('Skip tour')}
          </button>
        </div>

        {slide === 0 && (
          <SlideWelcome fullName={fullName} avatarUrl={avatarUrl} initials={initials} />
        )}
        {slide === 1 && <SlideStart />}
        {slide === 2 && <SlideProof />}

        {err && <div className="text-danger text-sm">{err}</div>}

        <button
          type="button"
          className="btn-accent w-full"
          onClick={next}
          disabled={busy}
          // biome-ignore lint/a11y/noAutofocus: the tour opens as a modal dialog, and focusing its primary action on mount is the standard dialog behavior — keyboard and screen-reader users land somewhere instead of behind the overlay
          autoFocus
        >
          {busy ? '…' : slide === TOTAL_SLIDES - 1 ? t('Got it') : t('Next')}
        </button>
      </div>
    </div>
  );
}

function SlideWelcome({
  fullName,
  avatarUrl,
  initials,
}: {
  fullName: string;
  avatarUrl?: string | null;
  initials: string;
}) {
  return (
    <div className="flex flex-col items-center gap-4 pt-2 text-center">
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt=""
          className="h-24 w-24 rounded-full object-cover ring-2 ring-ring"
          draggable={false}
        />
      ) : (
        <div className="flex h-24 w-24 items-center justify-center rounded-full bg-secondary font-heading font-bold tracking-tight text-3xl text-foreground ring-2 ring-ring">
          {initials}
        </div>
      )}
      <h2 className="font-heading font-bold tracking-tight text-2xl text-foreground">
        {t('Welcome, {{name}}').replace('{{name}}', fullName)}
      </h2>
      <p className="text-sm leading-6 text-muted-foreground">
        {t("Here's how famanage works for you.")}
      </p>
    </div>
  );
}

function SlideStart() {
  return (
    <div className="flex flex-col items-center gap-4 pt-2 text-center">
      <IconCircle>
        {/* Hand-pointing-finger glyph — drawn inline to avoid a new asset.
            Stroke + fill use the same ink token as the rest of the page. */}
        <svg viewBox="0 0 48 48" className="h-12 w-12" aria-hidden="true">
          <path
            d="M20 6v18l-4-4-3 3 11 11h11l4-12V14a2 2 0 0 0-4 0v6h-1V8a2 2 0 0 0-4 0v12h-1V6a2 2 0 0 0-4 0v14h-1V10a2 2 0 0 0-4 0v0"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </svg>
      </IconCircle>
      <h2 className="font-heading font-bold tracking-tight text-2xl text-foreground">
        {t('Tap a task when you start it')}
      </h2>
      <p className="text-sm leading-6 text-muted-foreground">
        {t('When you\'re working on something, tap "I\'m starting". Your owner sees instantly.')}
      </p>
    </div>
  );
}

function SlideProof() {
  return (
    <div className="flex flex-col items-center gap-4 pt-2 text-center">
      <IconCircle>
        {/* Camera + voice combo — simple line-art, matches the gold-on-ink
            brand language without pulling in a new icon library. */}
        <svg viewBox="0 0 48 48" className="h-12 w-12" aria-hidden="true">
          <rect
            x="4"
            y="14"
            width="28"
            height="20"
            rx="3"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          />
          <circle cx="18" cy="24" r="6" fill="none" stroke="currentColor" strokeWidth="2" />
          <path
            d="M12 14l3-4h6l3 4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <rect
            x="36"
            y="10"
            width="6"
            height="14"
            rx="3"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          />
          <path
            d="M34 22a5 5 0 0 0 10 0M39 27v5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </IconCircle>
      <h2 className="font-heading font-bold tracking-tight text-2xl text-foreground">
        {t("Send proof when you're done")}
      </h2>
      <p className="text-sm leading-6 text-muted-foreground">
        {t('Take a photo or record a voice note when you finish. Your owner approves it.')}
      </p>
    </div>
  );
}

function IconCircle({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-24 w-24 items-center justify-center rounded-full bg-secondary text-foreground ring-2 ring-ring">
      {children}
    </div>
  );
}

function Dots({ current, total }: { current: number; total: number }) {
  // Plain dots, no animation — keeps the visual quiet so the copy reads first.
  // dir="ltr" so the order doesn't flip under RTL (the slide order is fixed).
  return (
    <div className="flex items-center gap-1.5" dir="ltr" aria-hidden="true">
      {Array.from({ length: total }).map((_, i) => (
        <span
          // biome-ignore lint/suspicious/noArrayIndexKey: these are positional progress dots generated from a count — the index IS the identity and the list never reorders
          key={i}
          className={
            i === current ? 'h-2 w-6 rounded-full bg-primary' : 'h-2 w-2 rounded-full bg-border'
          }
        />
      ))}
    </div>
  );
}

function makeInitials(name: string): string {
  // First letter of the first two whitespace-separated tokens, uppercased.
  // Works for "Maria Santos" → MS, "Aisha" → A, "מרים ברק" → מב (Hebrew
  // doesn't have case, .toUpperCase() is a no-op).
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return (
    parts
      .map((p) => p[0] ?? '')
      .join('')
      .toUpperCase() || '?'
  );
}
